import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '@/hooks/useApi';
import { useAuth } from '@/hooks/useAuth';
import { PageContainer } from '@/components/common/PageContainer';
import { DataTable, type Column } from '@/components/common/DataTable';
import { LoadingSkeleton } from '@/components/common/LoadingSkeleton';
import { EmptyState } from '@/components/common/EmptyState';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Plus, Star, Pencil, Trash2, Building2, Users, Filter, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { Warehouse } from '@/types/warehouse.types';
import { AddWarehouseModal } from '@/components/features/warehouses/AddWarehouseModal';
import { EditWarehouseModal } from '@/components/features/warehouses/EditWarehouseModal';
import { WarehouseFilterSidebar, WarehouseFilterValues } from '@/components/features/warehouses/WarehouseFilterSidebar';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';

interface Tenant {
  id: string;
  name: string;
}

const initialFilters: WarehouseFilterValues = {
  search: '',
  status: '',
  type: '',
  city: '',
};

export default function WarehousesList() {
  const api = useApi();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState<string>('');
  const [loadingTenants, setLoadingTenants] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedWarehouse, setSelectedWarehouse] = useState<Warehouse | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [warehouseToDelete, setWarehouseToDelete] = useState<Warehouse | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [filters, setFilters] = useState<WarehouseFilterValues>(initialFilters);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [selectedRows, setSelectedRows] = useState<(string | number)[]>([]);
  const [hasFetched, setHasFetched] = useState(false);

  // Fetch tenants on mount (tenant role users use their own tenant_id directly)
  useEffect(() => {
    if (user?.role === 'tenant' && user.tenant_id) {
      setSelectedTenantId(user.tenant_id);
      return;
    }

    const fetchTenants = async () => {
      setLoadingTenants(true);
      try {
        const response: any = await api.get('/api/v1/dashboard/tenants');
        const tenantList = response?.data?.list || response?.list || response?.data || [];
        if (Array.isArray(tenantList)) {
          setTenants(tenantList);
          const defaultTenantId = tenantList.length > 0 ? tenantList[0].id : '';
          setSelectedTenantId(defaultTenantId);
        }
      } catch (error) {
        console.error('Failed to fetch tenants:', error);
        toast.error('فشل في جلب العملاء');
      } finally {
        setLoadingTenants(false);
      }
    };
    fetchTenants();
  }, []);

  const tenantId = selectedTenantId;

  const fetchWarehouses = useCallback(async () => {
    if (!tenantId) return;

    try {
      const response: any = await api.get(`/api/v1/dashboard/tenants/${tenantId}/warehouses`);
      if (response?.data && Array.isArray(response.data)) {
        setWarehouses(response.data);
      } else if (Array.isArray(response)) {
        setWarehouses(response);
      }
    } catch (error) {
      console.error('Failed to fetch warehouses:', error);
      toast.error('فشل في جلب المستودعات');
    } finally {
      setHasFetched(true);
    }
  }, [tenantId, api.get]);

  useEffect(() => {
    fetchWarehouses();
  }, [fetchWarehouses]);

  const handleEdit = (warehouse: Warehouse) => {
    setSelectedWarehouse(warehouse);
    setShowEditModal(true);
  };

  const handleDeleteClick = (warehouse: Warehouse) => {
    setWarehouseToDelete(warehouse);
    setShowDeleteDialog(true);
  };

  const handleDelete = async () => {
    if (!warehouseToDelete || !tenantId) return;

    setDeleting(true);
    try {
      await api.delete(`/api/v1/dashboard/tenants/${tenantId}/warehouses/${warehouseToDelete.id}`);
      toast.success('تم حذف المستودع بنجاح');
      fetchWarehouses();
    } catch (error: any) {
      const message = error?.response?.data?.message || 'فشل في حذف المستودع';
      if (message.includes('last warehouse')) {
        toast.error('لا يمكن حذف المستودع الأخير. أضف مستودع آخر أولاً.');
      } else {
        toast.error(message);
      }
    } finally {
      setDeleting(false);
      setShowDeleteDialog(false);
      setWarehouseToDelete(null);
    }
  };

  const handleSetDefault = async (warehouse: Warehouse) => {
    if (!tenantId || warehouse.is_default) return;

    try {
      await api.patch(`/api/v1/dashboard/tenants/${tenantId}/warehouses/${warehouse.id}`, {
        is_default: true,
      });
      toast.success('تم تعيين المستودع كافتراضي');
      fetchWarehouses();
    } catch (error: any) {
      const message = error?.response?.data?.message || 'فشل في تعيين المستودع كافتراضي';
      toast.error(message);
    }
  };

  const handleFilterChange = (key: keyof WarehouseFilterValues, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value === 'all' ? '' : value }));
  };

  const handleClearFilters = () => {
    setFilters(initialFilters);
  };

  // Client-side filtering
  const filteredWarehouses = warehouses.filter((w) => {
    if (filters.search) {
      const q = filters.search.toLowerCase();
      if (
        !(w.name || '').toLowerCase().includes(q) &&
        !(w.code || '').toLowerCase().includes(q)
      ) return false;
    }
    if (filters.status) {
      const isActive = filters.status === 'true';
      if (w.is_active !== isActive) return false;
    }
    if (filters.type && w.type !== filters.type) return false;
    if (filters.city) {
      const cityName = w.city_name?.ar || '';
      if (!cityName.toLowerCase().includes(filters.city.toLowerCase())) return false;
    }
    return true;
  });

  const columns: Column<Warehouse>[] = [
    {
      header: 'الرمز',
      accessor: (row) => (
        <span className="font-mono text-sm">{row.code}</span>
      ),
    },
    {
      header: 'الاسم',
      accessor: (row) => (
        <div className="flex items-center gap-2">
          <span className="font-medium">{row.name}</span>
          {row.is_default && (
            <Badge variant="secondary" className="gap-1">
              <Star className="h-3 w-3 fill-current" />
              افتراضي
            </Badge>
          )}
        </div>
      ),
    },
    {
      header: 'النوع',
      accessor: (row) => (
        <Badge variant="outline">
          {row.type === 'main' ? 'رئيسي' : 'فرعي'}
        </Badge>
      ),
    },
    {
      header: 'المدينة',
      accessor: (row) => row.city_name?.ar || '-',
    },
    {
      header: 'العنوان',
      accessor: (row) => (
        <span className="text-sm text-muted-foreground truncate max-w-[200px] block">
          {row.address}
        </span>
      ),
    },
    {
      header: 'الحالة',
      accessor: (row) => (
        <Badge variant={row.is_active ? 'default' : 'secondary'}>
          {row.is_active ? 'نشط' : 'غير نشط'}
        </Badge>
      ),
    },
  ];

  const renderActions = (warehouse: Warehouse) => (
    <>
      {!warehouse.is_default && (
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleSetDefault(warehouse)} title="تعيين كافتراضي">
          <Star className="h-4 w-4" />
        </Button>
      )}
      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleEdit(warehouse)} title="تعديل">
        <Pencil className="h-4 w-4" />
      </Button>
      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => handleDeleteClick(warehouse)} title="حذف">
        <Trash2 className="h-4 w-4" />
      </Button>
    </>
  );

  const FilterSidebarContent = (
    <WarehouseFilterSidebar
      filters={filters}
      onFilterChange={handleFilterChange}
      onClearFilters={handleClearFilters}
    />
  );

  return (
    <PageContainer>
      {/* Header */}
      <div className="mb-4 md:mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ChevronRight className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl md:text-2xl font-bold">المستودعات</h1>
              <p className="text-sm text-muted-foreground">إدارة المستودعات والتخزين</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Sheet open={showMobileFilters} onOpenChange={setShowMobileFilters}>
              <SheetTrigger asChild>
                <Button variant="outline" size="icon" className="lg:hidden">
                  <Filter className="h-4 w-4" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72 p-4">
                <div className="mt-6">{FilterSidebarContent}</div>
              </SheetContent>
            </Sheet>
            <Button onClick={() => setShowAddModal(true)} className="gap-2" disabled={!selectedTenantId}>
              <Plus className="h-4 w-4" />
              إضافة مستودع
            </Button>
          </div>
        </div>
      </div>

      {/* Tenant Selector (hidden for tenant users) */}
      {user?.role !== 'tenant' && (
        <div className="flex items-center gap-4 p-4 bg-muted/50 rounded-lg border mb-4">
          <Users className="h-5 w-5 text-muted-foreground" />
          <Label className="font-medium">العميل:</Label>
          <Select
            value={selectedTenantId}
            onValueChange={setSelectedTenantId}
            disabled={loadingTenants}
          >
            <SelectTrigger className="w-[280px] bg-background">
              <SelectValue placeholder={loadingTenants ? "جاري التحميل..." : "اختر العميل"} />
            </SelectTrigger>
            <SelectContent>
              {tenants.map((tenant) => (
                <SelectItem key={tenant.id} value={tenant.id}>
                  {tenant.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Main Content with Sidebar */}
      <div className="flex gap-4 ">
        <div className="hidden lg:block">
          {FilterSidebarContent}
        </div>
        <div className="flex-1 min-w-0">
          {!hasFetched && api.loading ? (
            <LoadingSkeleton type="table" rows={8} />
          ) : filteredWarehouses.length === 0 ? (
            <EmptyState
              icon={<Building2 className="h-16 w-16" />}
              title="لا توجد مستودعات"
              description="أضف مستودعك الأول للبدء في إدارة الشحنات"
              action={{ label: 'إضافة مستودع', onClick: () => setShowAddModal(true) }}
            />
          ) : (
            <DataTable
              data={filteredWarehouses}
              columns={columns}
              actions={renderActions}
              selectable={true}
              selectedRows={selectedRows}
              onSelectionChange={setSelectedRows}
            />
          )}
        </div>
      </div>

      {/* Add Warehouse Modal */}
      <AddWarehouseModal
        open={showAddModal}
        onOpenChange={setShowAddModal}
        tenantId={tenantId || ''}
        onSuccess={fetchWarehouses}
      />

      {/* Edit Warehouse Modal */}
      {selectedWarehouse && (
        <EditWarehouseModal
          open={showEditModal}
          onOpenChange={(open) => {
            setShowEditModal(open);
            if (!open) setSelectedWarehouse(null);
          }}
          tenantId={tenantId || ''}
          warehouse={selectedWarehouse}
          onSuccess={fetchWarehouses}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>حذف المستودع</AlertDialogTitle>
            <AlertDialogDescription>
              {warehouseToDelete?.is_default ? (
                <span className="text-warning">
                  تحذير: هذا هو المستودع الافتراضي. سيتم تعيين مستودع آخر كافتراضي تلقائياً.
                </span>
              ) : (
                <>هل أنت متأكد من حذف المستودع "{warehouseToDelete?.name}"؟</>
              )}
              <br />
              لا يمكن التراجع عن هذا الإجراء.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>إلغاء</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? 'جاري الحذف...' : 'حذف'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageContainer>
  );
}
